import type { Locale } from '@skytech/types'

import { MS_PER_DAY } from './constants'
import { getBrowserLocale, LOCALE_MAP } from './locale'

export const getLocalDate = ({ locale, date }: { date: string | Date | null; locale?: Locale }) => {
  if (!date) return '-'

  const browserLocale = getBrowserLocale()
  let dateLocale: string = locale || browserLocale
  let safeLocale = LOCALE_MAP[dateLocale as Locale] || 'en-GB'

  const dateToFormat = typeof date === 'string' ? new Date(date) : date
  return dateToFormat.toLocaleDateString(safeLocale)
}

export const getLocalDateTime = ({
  locale,
  date,
  showDate = true,
}: {
  date: string | Date | null
  locale?: Locale
  showDate?: boolean
}) => {
  if (!date) return '-'

  const browserLocale = getBrowserLocale()
  let dateLocale: string = locale || browserLocale
  let safeLocale = LOCALE_MAP[dateLocale as Locale] || 'en-GB'

  let options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: 'numeric',
  }
  if (showDate) {
    options = { ...options, day: 'numeric', month: 'numeric', year: 'numeric' }
  }

  const dateToFormat = typeof date === 'string' ? new Date(date) : date
  return dateToFormat.toLocaleTimeString(safeLocale, options)
}

export const getLocaleTimezoneIsoDate = (date: string | Date) => {
  if (!date) return '-'

  const dateToFormat = typeof date === 'string' ? new Date(date) : date
  if (isNaN(dateToFormat.getTime())) return ''

  dateToFormat.setMinutes(dateToFormat.getMinutes() - dateToFormat.getTimezoneOffset())

  return dateToFormat.toISOString().slice(0, 10)
}

export const getLocaleTimezoneIsoDateTime = (date: string) => {
  const dateToParse = new Date(date)

  dateToParse.setMinutes(dateToParse.getMinutes() - dateToParse.getTimezoneOffset())

  return dateToParse.toISOString().slice(0, 16)
}

/**
 *
 * @param input
 * @param format
 * Use the following placeholders for the formats:
 * Y - years,
 * M - months,
 * D - date,
 * H - hours,
 * m - minutes,
 * s - seconds,
 * @returns formated date
 */
export const getDateFormat = (input: Date, format: string): string => {
  // Replace the placeholders with actual date components
  const formattedDate = format
    .replace('YYYY', String(input.getFullYear()))
    .replace('YY', String(input.getFullYear()).substring(2, 4))
    .replace('MM', (input.getMonth() + 1).toString().padStart(2, '0'))
    .replace('DD', input.getDate().toString().padStart(2, '0'))
    .replace('HH', input.getHours().toString().padStart(2, '0'))
    .replace('mm', input.getMinutes().toString().padStart(2, '0'))
    .replace('ss', input.getSeconds().toString().padStart(2, '0'))

  return formattedDate
}

export function isOlderThan(dateString: string, days: number) {
  // Parse the input date
  const inputDate = new Date(dateString)

  // Get the current date and time
  const now = new Date()

  // Calculate the difference in milliseconds
  const diffInMilliseconds = now.getTime() - inputDate.getTime()

  // Convert milliseconds to days
  const diffInDays = diffInMilliseconds / MS_PER_DAY

  // Check if the difference is greater than the specified number of days
  return diffInDays > days
}

export function getMinDate(months = 1, referenceDate = new Date()) {
  const minDate = new Date(referenceDate)
  minDate.setMonth(referenceDate.getMonth() - months)
  return minDate
}

export function getMaxDate(months = 1, referenceDate = new Date()) {
  const maxDate = new Date(referenceDate)
  maxDate.setMonth(referenceDate.getMonth() + months)
  return maxDate
}

// todo: test and remove
export const oneYearFromNow = (today: Date): Date => {
  const nextYear = new Date(today)
  nextYear.setFullYear(today.getFullYear() + 1)
  return nextYear
}

export const getIsDateInRange = ({
  minDate,
  maxDate,
  options,
}: {
  minDate: Date
  maxDate: Date
  options: { date: Date }
}) => {
  const date = options.date || new Date()
  if (isNaN(date.getTime())) return false
  return toDateOnly(minDate) <= toDateOnly(date) && toDateOnly(date) <= toDateOnly(maxDate)
}

export const toDateOnly = (date: Date) => date.toISOString().split('T')[0]

export const toUtcDate = (date: Date | string): Date => {
  const d = new Date(date)
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
}
